import { StandardEvents } from '@shopify/events';

/*
 * Keeps the price in step with the variant picker.
 *
 * Horizon applies only the variant picker on a normal product page -- its
 * `morphElementSelector` is undefined there -- so every other part of the page
 * is responsible for updating itself. Dawn does the opposite: it re-renders the
 * section and copies elements across by id, which is why a plain block written
 * for Dawn shows the right price there and a stale one here.
 *
 * Four figures move, and the last two are claims about money rather than
 * decoration: the price, the compare-at, the discount percentage and the
 * saving. Every variant's are already in the page as JSON, formatted by
 * Shopify's `money` filter, so this swaps strings and never calculates a price.
 *
 * Follows `pdp-specs.js`, deliberately: one convention for keeping a
 * server-rendered block in step, not two.
 */
class PdpPriceLive extends HTMLElement {
  connectedCallback() {
    const source = this.querySelector('[data-pdp-price-map]');
    if (!source) return;

    try {
      this.map = JSON.parse(source.textContent);
    } catch (error) {
      // Leave the server-rendered price alone. A wrong price is far worse than
      // one that does not follow the picker, so a broken map must not blank it.
      console.warn('[pdp-price] variant map is not valid JSON:', error);
      return;
    }

    this.now = this.querySelector('.pdp-price__now');
    this.was = this.querySelector('.pdp-price__was');
    this.chip = this.querySelector('.pdp-price__chip');
    this.chipPct = this.chip?.querySelector('.pdp-price__chip-pct');
    this.chipSaving = this.chip?.querySelector('.pdp-price__chip-saving > span');
    this.saving = this.querySelector('.pdp-price__saving');
    this.savingAmount = this.saving?.querySelector('strong');
    this.coupon = this.querySelector('.pdp-price__coupon');

    this.target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    if (!this.target) return;
    this.target.addEventListener(StandardEvents.productSelect, this.handleSelect);
  }

  disconnectedCallback() {
    this.target?.removeEventListener(StandardEvents.productSelect, this.handleSelect);
  }

  /* The id arrives either as a bare number from the picker or as a
     gid://shopify/ProductVariant/123 from elsewhere. Both end in the digits the
     map is keyed by, so the tail is what gets used. */
  static variantId(value) {
    const match = String(value ?? '').match(/(\d+)\s*$/);
    return match ? match[1] : null;
  }

  static toggle(element, visible) {
    if (!element) return;
    element.hidden = !visible;
  }

  handleSelect = (event) => {
    const id = PdpPriceLive.variantId(event?.detail?.resource?.id ?? event?.detail?.variantId);
    if (!id) return;

    const price = this.map?.[id];
    // A miss leaves every figure as it was. Showing the previous variant's
    // price is wrong, but showing a blank or a zero is worse, and an unknown
    // id means this block cannot say anything true about it.
    if (!price) return;

    if (this.now) this.now.textContent = price.now;

    if (this.was) {
      this.was.textContent = price.was;
      PdpPriceLive.toggle(this.was, price.onSale);
    }

    if (this.chip) {
      // Write into the parts, not the chip, so the saving span survives.
      if (this.chipPct) this.chipPct.textContent = price.chip;
      if (this.chipSaving) this.chipSaving.textContent = price.saving;
      PdpPriceLive.toggle(this.chip, Boolean(price.chip));
    }

    if (this.saving) {
      if (this.savingAmount) this.savingAmount.textContent = price.saving;
      PdpPriceLive.toggle(this.saving, price.onSale && Boolean(price.saving));
    }

    if (this.coupon) {
      const hasCoupon = Boolean(price.coupon && price.couponPrice);
      if (hasCoupon) {
        this.coupon.querySelector('[data-coupon-code]').textContent = price.coupon;
        this.coupon.querySelector('[data-coupon-price]').textContent = price.couponPrice;
        const headline = this.coupon.querySelector('[data-coupon-headline]');
        if (headline && price.couponHeadline) headline.textContent = price.couponHeadline;
        // The coupon card's copy chip copies from its attribute, not its text.
        this.coupon.querySelectorAll('[data-pdp-offer-code]').forEach((button) => {
          button.setAttribute('data-pdp-offer-code', price.coupon);
        });
      }
      PdpPriceLive.toggle(this.coupon, hasCoupon);
    }
  };
}

if (!customElements.get('pdp-price-live')) {
  customElements.define('pdp-price-live', PdpPriceLive);
}
